import { useCallback, useEffect, useState } from 'react';

type HelpContent = Record<string, string>;

/** The article bodies live in their own chunk (≈300 KB); loaded once, shared. */
let cached: Promise<HelpContent> | null = null;

const loadContent = (): Promise<HelpContent> => {
    if (!cached) {
        cached = import('../../generated/helpCenterContent.json')
            .then((module) => (module.default ?? module) as unknown as HelpContent)
            .catch((error) => {
                // A failed chunk load must be retryable, not cached forever.
                cached = null;
                throw error;
            });
    }
    return cached;
};

export interface HelpContentState {
    content: HelpContent | null;
    loading: boolean;
    /** True when the chunk failed to load — shown as an error with a retry,
     *  never as an empty article. */
    failed: boolean;
    retry: () => void;
}

export const useHelpContent = (): HelpContentState => {
    const [content, setContent] = useState<HelpContent | null>(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let active = true;
        setFailed(false);
        loadContent()
            .then((loaded) => {
                if (active) setContent(loaded);
            })
            .catch(() => {
                if (active) setFailed(true);
            });
        return () => {
            active = false;
        };
    }, [attempt]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    return { content, loading: !content && !failed, failed, retry };
};
